import * as Schema from "effect/Schema";
import { PreviewTarget } from "./target.js";
import { ContentHash, Dimensions, Finite, HexColor, Identifier, NonEmptyText, NonNegative, Point, Positive, Rect, Revision, SafeInteger, TimeMs, UnitInterval, freezeMetadata, hasUniqueIds, rectFits, } from "@linempo/kernel";
export const RENDER_PLAN_VERSION = 1;
export const RENDERER_VERSION = "canvas-v1";
export const PathDescriptor = Schema.Struct({
    id: Identifier,
    bufferId: Identifier,
    /** Vertex offset into packed Float64 triples [x, y, cumulativeLength]. */
    vertexOffset: SafeInteger,
    vertexCount: SafeInteger.check(Schema.isGreaterThan(0)),
    length: NonNegative,
});
export const RasterDescriptor = Schema.Struct({
    id: Identifier,
    bufferId: Identifier,
    assetHash: ContentHash,
    dimensions: Dimensions,
    format: Schema.Literal("rgba8"),
});
export const MaskDescriptor = Schema.Struct({
    id: Identifier,
    bufferId: Identifier,
    dimensions: Dimensions,
    format: Schema.Literal("alpha8"),
});
export const PlannedProtection = Schema.Struct({ id: Identifier, rect: Rect });
const OperationBase = {
    id: Identifier,
    sceneId: Identifier,
    /** Exclusion regions apply to draw/fill/erase AFTER feathering. */
    protectionIds: Schema.Array(Identifier),
};
export const DrawStroke = Schema.TaggedStruct("DrawStroke", {
    ...OperationBase,
    startMs: TimeMs,
    endMs: TimeMs,
    pathId: Identifier,
    /** null draws a vector stroke; otherwise reveals the original source RGBA. */
    rasterId: Schema.NullOr(Identifier),
    /** Explicit automatic-stroke finish: at progress 1 reveal the source raster
     * in full, still excluding protectionIds. Omitted/false retains path-only
     * reveal. This preserves tiny details lost by thinning without changing it.
     */
    revealAllAtEnd: Schema.optionalKey(Schema.Boolean),
    sourcePlacement: Rect,
    width: Positive,
    feather: NonNegative,
    color: HexColor,
}).check(Schema.makeFilter((op) => {
    if (op.endMs <= op.startMs)
        return "draw duration must be positive";
    if (op.revealAllAtEnd && op.rasterId === null)
        return "full raster finish requires a source raster";
    return undefined;
}));
export const FillRegion = Schema.TaggedStruct("FillRegion", {
    ...OperationBase,
    startMs: TimeMs,
    endMs: TimeMs,
    rect: Rect,
    /** null means a rectangular fill; alpha8 mask is scaled into rect otherwise. */
    maskId: Schema.NullOr(Identifier),
    color: HexColor,
}).check(Schema.makeFilter((op) => op.endMs > op.startMs || "fill duration must be positive"));
export const EraseRegion = Schema.TaggedStruct("EraseRegion", {
    ...OperationBase,
    atMs: TimeMs,
    rect: Rect,
    mode: Schema.Literal("step"),
});
export const RenderOperation = Schema.Union([DrawStroke, FillRegion, EraseRegion]);
export const PlannedScene = Schema.Struct({ id: Identifier, startMs: TimeMs, endMs: TimeMs }).check(Schema.makeFilter((scene) => scene.endMs > scene.startMs || "scene duration must be positive"));
export const PlannedCaption = Schema.Struct({
    id: Identifier, startMs: TimeMs, endMs: TimeMs, text: NonEmptyText,
}).check(Schema.makeFilter((cue) => cue.endMs > cue.startMs || "caption duration must be positive"));
export const CaptionStyle = Schema.Struct({
    fontFamily: NonEmptyText,
    fontVersion: NonEmptyText,
    fontSize: Positive,
    lineHeight: Positive,
    color: HexColor,
    background: HexColor,
    bottomInset: NonNegative,
    maxWidth: Positive,
});
export const HandStyle = Schema.Struct({
    enabled: Schema.Boolean,
    preset: Schema.Literal("pen-v1"),
    /** Offset from sampled tip in design-canvas pixels; angle follows the path. */
    anchorOffset: Point,
    scale: Positive,
});
export const operationStartMs = (op) => op._tag === "EraseRegion" ? op.atMs : op.startMs;
export const RenderPlan = Schema.Struct({
    schemaVersion: Schema.Literal(RENDER_PLAN_VERSION),
    id: Identifier,
    projectId: Identifier,
    projectRevision: Revision,
    /** Old stored plans omit target and are timeline plans. */
    target: Schema.optionalKey(PreviewTarget),
    canvas: Dimensions,
    /** Includes initial gaps and explicit tail hold; frame generation starts at t=0. */
    durationMs: TimeMs.check(Schema.isGreaterThan(0)),
    background: HexColor,
    seed: SafeInteger,
    algorithmVersion: NonEmptyText,
    rendererVersion: Schema.Literal(RENDERER_VERSION),
    assetHashes: Schema.Array(ContentHash),
    paths: Schema.Array(PathDescriptor),
    rasters: Schema.Array(RasterDescriptor),
    masks: Schema.Array(MaskDescriptor),
    protections: Schema.Array(PlannedProtection),
    scenes: Schema.Array(PlannedScene),
    /** Nondecreasing start time; stable array order resolves ties and overlap. */
    operations: Schema.Array(RenderOperation),
    /** Active captions stack in array order; intervals are [startMs, endMs). */
    captions: Schema.Array(PlannedCaption),
    captionStyle: CaptionStyle,
    hand: HandStyle,
}).check(Schema.makeFilter((plan) => {
    for (const items of [plan.paths, plan.rasters, plan.masks, plan.protections, plan.scenes, plan.operations, plan.captions]) {
        if (!hasUniqueIds(items))
            return "duplicate plan descriptor ID";
    }
    const paths = new Set(plan.paths.map((path) => path.id));
    const rasters = new Set(plan.rasters.map((raster) => raster.id));
    const masks = new Set(plan.masks.map((mask) => mask.id));
    const protections = new Set(plan.protections.map((region) => region.id));
    const scenes = new Map(plan.scenes.map((scene) => [scene.id, scene]));
    const hashes = new Set(plan.assetHashes);
    if (plan.rasters.some((raster) => !hashes.has(raster.assetHash)))
        return "raster hash is missing from plan inputs";
    if (plan.protections.some((region) => !rectFits(region.rect, plan.canvas)))
        return "protection exceeds canvas";
    if ([...plan.scenes, ...plan.captions].some((item) => item.endMs > plan.durationMs))
        return "scene or caption exceeds plan duration";
    let lastStart = -1;
    for (const op of plan.operations) {
        const start = operationStartMs(op);
        const scene = scenes.get(op.sceneId);
        if (!scene)
            return "operation refers to unknown scene";
        if (start < lastStart)
            return "operations must be ordered by absolute start time";
        lastStart = start;
        if (start > plan.durationMs || start < scene.startMs)
            return "operation time is outside plan or scene";
        if (op.protectionIds.some((id) => !protections.has(id)))
            return "unknown operation protection";
        if (op._tag === "EraseRegion") {
            if (op.atMs < scene.endMs)
                return "step erase must not precede scene end";
            if (!rectFits(op.rect, plan.canvas))
                return "erase rect exceeds canvas";
        }
        else {
            if (op.endMs > scene.endMs)
                return "operation exceeds scene end";
            if (op._tag === "DrawStroke") {
                if (!paths.has(op.pathId) || op.rasterId !== null && !rasters.has(op.rasterId))
                    return "unknown path or raster";
                if (!rectFits(op.sourcePlacement, plan.canvas))
                    return "source placement exceeds canvas";
            }
            else {
                if (op.maskId !== null && !masks.has(op.maskId))
                    return "unknown fill mask";
                if (!rectFits(op.rect, plan.canvas))
                    return "fill rect exceeds canvas";
            }
        }
    }
    return undefined;
}));
export const decodeRenderPlan = (input) => freezeMetadata(Schema.decodeUnknownSync(RenderPlan)(input));
/** Seeking always starts from a blank background, then replays this stable list. */
export function operationsAt(plan, timeMs) {
    Schema.decodeUnknownSync(TimeMs)(timeMs);
    return plan.operations.filter((op) => operationStartMs(op) <= timeMs).map((operation) => ({
        operation,
        progress: operation._tag === "EraseRegion" ? 1 : Math.min(1, (timeMs - operation.startMs) / (operation.endMs - operation.startMs)),
    }));
}
export const activeCaptions = (plan, timeMs) => {
    Schema.decodeUnknownSync(TimeMs)(timeMs);
    return plan.captions.filter((cue) => cue.startMs <= timeMs && timeMs < cue.endMs);
};
export const Opacity = UnitInterval;
export const Angle = Finite;
